import type { PullPlanTaktSectionRef, ScopeOverlap, TaktSection } from "../../../models";

export interface SectionCardSummary {
  name: string;
  taktA: string;
  sortA: number;
  wbsRoot: string | null;
  activityCount: number;
  workCodeCount: number;
  zoneCount: number;
  earliestStart: string | null;
  latestFinish: string | null;
  workdayCount: number;
  /** The WBS leaves feeding this section (the plan's scope). */
  wbsNodes: Array<{ id: number; name: string | null }>;
  /** Full scope membership (placed + not-yet-placed), from the spine ref. */
  scopeMemberCount: number;
  /** Scope members already tagged into this section (vs untagged). Drives the
   *  card's scope-breakdown bar. */
  taggedCount: number;
  untaggedCount: number;
  /** Other ACTIVE drafts sharing leaves with this section. */
  scopeOverlaps: ScopeOverlap[];
}

export function summarizeSection(
  section: TaktSection,
  workdays: string[],
  ref: PullPlanTaktSectionRef,
): SectionCardSummary {
  let activityCount = 0;
  const workCodeSet = new Set<string>();
  let earliestStart: string | null = null;
  let latestFinish: string | null = null;

  for (const group of section.groups) {
    for (const entry of group.entries) {
      activityCount += entry.activity_ids.length;
      if (entry.work_code) workCodeSet.add(entry.work_code);
      if (entry.start && (!earliestStart || entry.start < earliestStart)) {
        earliestStart = entry.start;
      }
      if (entry.finish && (!latestFinish || entry.finish > latestFinish)) {
        latestFinish = entry.finish;
      }
    }
  }

  let workdayCount = 0;
  if (earliestStart && latestFinish) {
    for (const d of workdays) {
      if (d >= earliestStart && d <= latestFinish) workdayCount++;
    }
  }

  return {
    name: section.name,
    taktA: section.takt_a,
    sortA: section.sort_a,
    wbsRoot: section.wbs_root,
    activityCount,
    workCodeCount: workCodeSet.size,
    zoneCount: section.groups.length,
    earliestStart,
    latestFinish,
    workdayCount,
    wbsNodes: ref.wbs_nodes ?? [],
    scopeMemberCount: (ref.tagged_count ?? 0) + (ref.untagged_count ?? 0),
    taggedCount: ref.tagged_count ?? 0,
    untaggedCount: ref.untagged_count ?? 0,
    scopeOverlaps: ref.scope_overlaps ?? [],
  };
}

/** Summary for a section the Takt board doesn't carry — i.e. one whose
 *  activities are all currently untagged (no work codes). It still exists via
 *  Takt_SectionName and stays pullable, so it shows as an empty card. */
export function emptySummaryFromRef(
  ref: PullPlanTaktSectionRef,
): SectionCardSummary {
  return {
    name: ref.name,
    taktA: ref.takt_a,
    sortA: ref.sort_a,
    wbsRoot: ref.wbs_root,
    activityCount: ref.activity_count,
    workCodeCount: 0,
    zoneCount: 0,
    earliestStart: null,
    latestFinish: null,
    workdayCount: 0,
    wbsNodes: ref.wbs_nodes ?? [],
    scopeMemberCount: (ref.tagged_count ?? 0) + (ref.untagged_count ?? 0),
    taggedCount: ref.tagged_count ?? 0,
    untaggedCount: ref.untagged_count ?? 0,
    scopeOverlaps: ref.scope_overlaps ?? [],
  };
}
